import {useContext, createContext, useState, useMemo, type PropsWithChildren} from 'react'
import {navigate} from 'wouter/use-browser-location'
import {Redirect} from 'wouter'

interface AuthUser {
  username: string
  access: number
}

type AuthContextType = {
  token: string | null
  user: AuthUser | null
  header: {Authroziation: string} | {}
  logIn: (username: string, password: string) => Promise<void>
  logOut: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({children}: React.PropsWithChildren) {
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'))

  const user = useMemo(() => {
    if (!token) return null

    const payloadPart = token.split('.')[1]
    if (!payloadPart) return null

    const payload = JSON.parse(atob(payloadPart))
    if (!payload.sub) return null

    return {username: payload.sub, access: payload.access}
  }, [token])

  const header = useMemo(() => (token ? {Authorization: 'Bearer ' + token} : {}), [token])

  const logIn = async (username: string, password: string) => {
    const response = await fetch('/api/signin', {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({username, password}),
    })
    const res = await response.json()

    if (res.error) throw new Error(res.error)
    if (!res.data?.token) throw new Error('Unexpected response')

    setToken(res.data.token)
    localStorage.setItem('token', res.data.token)

    navigate('/')
  }

  const logOut = () => {
    setToken(null)
    localStorage.removeItem('token')
    navigate('/signin')
  }

  const value: AuthContextType = {
    token,
    user,
    header,
    logIn,
    logOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within an AuthProvider')

  return context
}

export function Private({children}: PropsWithChildren) {
  const user = useAuth()
  if (!user.token) return <Redirect to='/signin' />
  return <>{children}</>
}
